import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type IncidentEventPriority = 'Critical' | 'Standard' | 'Info'

export const NEW_INCIDENT_HIGHLIGHT_MS = 8000

export type IncidentArrival = {
  incidentId: number
  arrivedAt: number
  priority: IncidentEventPriority
}

type NewIncidentsState = {
  arrivals: IncidentArrival[]
  unseenCount: number
  soundEnabled: boolean
  desktopEnabled: boolean
  recordArrival: (arrival: IncidentArrival) => void
  pruneExpired: () => void
  markAllSeen: () => void
  setSoundEnabled: (enabled: boolean) => void
  setDesktopEnabled: (enabled: boolean) => void
}

function withoutExpired(arrivals: IncidentArrival[], now: number): IncidentArrival[] {
  return arrivals.filter((a) => now - a.arrivedAt < NEW_INCIDENT_HIGHLIGHT_MS)
}

export const useNewIncidentsStore = create<NewIncidentsState>()(
  persist(
    (set, get) => ({
      arrivals: [],
      unseenCount: 0,
      soundEnabled: true,
      desktopEnabled: false,
      recordArrival: (arrival) =>
        set((state) => {
          const now = Date.now()
          const kept = withoutExpired(state.arrivals, now).filter(
            (a) => a.incidentId !== arrival.incidentId,
          )
          return {
            arrivals: [...kept, arrival],
            unseenCount: state.unseenCount + 1,
          }
        }),
      pruneExpired: () => {
        const now = Date.now()
        const current = get().arrivals
        const pruned = withoutExpired(current, now)
        if (pruned.length !== current.length) set({ arrivals: pruned })
      },
      markAllSeen: () => set({ unseenCount: 0 }),
      setSoundEnabled: (soundEnabled) => set({ soundEnabled }),
      setDesktopEnabled: (desktopEnabled) => set({ desktopEnabled }),
    }),
    {
      name: 'new-incidents-settings',
      partialize: (state) => ({
        soundEnabled: state.soundEnabled,
        desktopEnabled: state.desktopEnabled,
      }),
    },
  ),
)
